import React, { useState, useEffect, useRef } from 'react';

type MovementMode = 'stationary' | 'walking' | 'driving' | 'flying';

interface GamePanelProps {
  onShareLocation: () => void;
  onUpdateLocation?: () => void;
  sessionLink: string;
  playerLocation: [number, number] | null;
  showOtherUsers: boolean;
  onToggleShowUsers: (show: boolean) => void;
  maxDistance: number;
  onDistanceChange: (distance: number) => void;
  otherUsersCount: number;
  /**
   * Count of AI agents currently rendered on the map (any watched
   * country). Distinct from `otherUsersCount` because agents arrive
   * through `AgentMarkerLayer` and don't share GeoLink session
   * filtering — they're shown regardless of `showOtherUsers`,
   * `maxDistance`, or trust groups. Optional so callers that don't
   * mount the agent layer (no map, e.g. in tests) get the existing
   * single-pill behaviour.
   */
  aiAgentsCount?: number;
  /** Wallets from loaded trust groups (for map filter); 0 hides the shield control */
  trustGroupMemberAllowlistCount?: number;
  /** When true, map shows only allowlisted wallets (see App + Trust Groups overlay) */
  trustGroupMapFilterActive?: boolean;
  onTrustGroupMapFilterToggle?: (active: boolean) => void;
  minimized?: boolean;
  onToggleMinimize?: () => void;
  isLiveTracking?: boolean;
  /** GPS-detected mode */
  movementMode?: MovementMode;
  /** Manual override ≥ detected, or same as detected if no override */
  effectiveMode?: MovementMode;
  /** True when following GPS automatically (no manual override active) */
  isAutoMode?: boolean;
  onSetMode?: (mode: MovementMode) => void;
  onClearMode?: () => void;
  onStartLiveTracking?: () => void;
  onStopLiveTracking?: () => void;
  onGoOffline?: () => void;
}

// Per-mode display config
const MODE_CONFIG: Record<MovementMode, { icon: string; color: string; label: string }> = {
  stationary: { icon: '📍', color: '#94a3b8', label: 'Stay Live' },
  walking:    { icon: '🚶', color: '#4CAF50', label: 'Walking'  },
  driving:    { icon: '🚗', color: '#FF6B35', label: 'Driving'  },
  flying:     { icon: '✈️', color: '#818cf8', label: 'Flying'   },
};

/**
 * Format a coordinate pair as an approximate (0.1°-quantised) region
 * label — e.g. `37.7°N · 122.4°W`.  Used in the GamePanel so the
 * logged-in user's privacy is preserved by default; they can still
 * reveal the exact lat/lng behind the eye toggle when they need it.
 *
 * Matches the formatter used in the live-broadcast HUD so the
 * on-screen language is consistent between the Game panel and the
 * ProfileOverlay's "~ Region" metric.
 */
function formatApproxLocationLabel(lat: number, lng: number): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '—';
  const latStr = `${Math.abs(lat).toFixed(1)}°${lat >= 0 ? 'N' : 'S'}`;
  const lngStr = `${Math.abs(lng).toFixed(1)}°${lng >= 0 ? 'E' : 'W'}`;
  return `${latStr} · ${lngStr}`;
}

export const GamePanel: React.FC<GamePanelProps> = ({
  onShareLocation,
  onUpdateLocation,
  sessionLink,
  playerLocation,
  showOtherUsers,
  onToggleShowUsers,
  maxDistance,
  onDistanceChange,
  otherUsersCount,
  aiAgentsCount,
  trustGroupMemberAllowlistCount = 0,
  trustGroupMapFilterActive = false,
  onTrustGroupMapFilterToggle,
  minimized = false,
  onToggleMinimize,
  isLiveTracking = false,
  movementMode = 'stationary',
  effectiveMode = 'stationary',
  isAutoMode = true,
  onSetMode,
  onClearMode,
  onStartLiveTracking,
  onStopLiveTracking,
  onGoOffline,
}) => {
  const [countdown, setCountdown] = useState<number>(10);
  const [copied, setCopied] = useState(false);
  // Precise coordinates are hidden by default.  The panel shows the
  // 0.1°-quantised approximate region ("~ 37.7°N · 122.4°W") and the
  // logged-in user can opt-in to reveal the full lat/lng with the eye
  // toggle — keeps the surface friendly when screen-sharing or when
  // bystanders are nearby.
  const [revealExactLocation, setRevealExactLocation] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef<number>(Date.now());

  const copyLink = () => {
    navigator.clipboard.writeText(sessionLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Auto-update every 10 s — suspended while Stay Live is active.
  // Depend only on the boolean (has location or not), NOT the coordinate values.
  // GPS ticks call setPlayerLocation with a new array reference every few seconds;
  // if we depended on the array itself the effect would restart and reset the
  // countdown each tick.
  const hasLocation = playerLocation !== null;
  useEffect(() => {
    if (!hasLocation) {
      setCountdown(10);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    setCountdown(10);
    lastUpdateTimeRef.current = Date.now();

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (!isLiveTracking && onUpdateLocation) onUpdateLocation();
          lastUpdateTimeRef.current = Date.now();
          return 10;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLocation, onUpdateLocation]);

  const handleGoLive = () => {
    // Share location (creates session) AND start continuous tracking simultaneously
    onShareLocation();
    onStartLiveTracking?.();
  };

  const handleManualUpdate = () => {
    if (playerLocation && onUpdateLocation) onUpdateLocation();
    setCountdown(10);
    lastUpdateTimeRef.current = Date.now();
  };

  const mode = MODE_CONFIG[effectiveMode];
  const MODE_RANK: Record<MovementMode, number> = { stationary: 0, walking: 1, driving: 2, flying: 3 };

  return (
    <div className="game-panel">
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: minimized ? '0' : '16px', gap: '8px' }}>
        {!minimized && (
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>Connected to GeoLink</div>
        )}
        {onToggleMinimize && (
          <button
            className="icon-button"
            onClick={onToggleMinimize}
            title={minimized ? 'Expand' : 'Minimize'}
            style={{ fontSize: '10px', padding: '1px 3px', width: '18px', height: '18px', flexShrink: 0 }}
          >
            {minimized ? '▼' : '▲'}
          </button>
        )}
      </div>

      {!minimized && (
        <>
          {/* ── Location action area ── */}

          {!playerLocation ? (

            /* STATE 1 — not yet live: single Go LIVE button, also starts tracking */
            <button
              className="primary-button"
              onClick={handleGoLive}
              style={{ padding: '8px 16px', fontSize: '14px', width: 'auto', minWidth: '140px' }}
            >
              Go LIVE
            </button>

          ) : isLiveTracking ? (

            /* STATE 3 — Stay Live active */
            <>
            <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: '4px' }}>

              {/* Status pill */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                flex: 1, minWidth: 0,
                padding: '9px 10px 9px 12px',
                borderRadius: '8px 0 0 8px',
                border: `1px solid ${mode.color}`,
                borderRight: 'none',
                background: `${mode.color}1a`,
                color: mode.color,
                fontSize: '13px', fontWeight: 700,
              }}>
                {/* Pulsing dot */}
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: mode.color,
                  animation: 'pulse 1.4s ease-in-out infinite',
                  flexShrink: 0,
                }} />
                {/* Mode icon + always show "Live" so user knows they're active */}
                <span style={{ fontSize: '14px', flexShrink: 0 }}>{mode.icon}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  You're Live
                </span>
                {/* Countdown — subtle, right-aligned */}
                <span style={{
                  marginLeft: 'auto', fontSize: '10px', opacity: 0.55,
                  fontFamily: 'monospace', fontWeight: 400, flexShrink: 0,
                }}>
                  {countdown}s
                </span>
              </div>

              {/* × stop — div avoids the CSS width:100% rule on buttons */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => onStopLiveTracking?.()}
                onKeyDown={e => e.key === 'Enter' && onStopLiveTracking?.()}
                title="End Live"
                style={{
                  width: '38px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  borderRadius: '0 8px 8px 0',
                  border: `1px solid ${mode.color}`,
                  borderLeft: `1px solid ${mode.color}44`,
                  background: 'transparent',
                  color: mode.color,
                  fontSize: '18px', fontWeight: 700,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                ×
              </div>
            </div>

            {/* ── Mode selector ── */}
            {/* ── Manual mode override ── Auto / Drive / Fly only
                Walking & Stationary are auto-detected; Drive & Fly can be
                manually set when GPS hasn't caught up or for drone use. */}
            <div style={{ display: 'flex', gap: '5px', marginTop: '6px' }}>

              {/* Auto — clears any manual override */}
              <div
                role="button" tabIndex={0}
                onClick={onClearMode}
                onKeyDown={e => e.key === 'Enter' && onClearMode?.()}
                title="Follow GPS automatically"
                style={{
                  flex: 1,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                  padding: '5px 4px', borderRadius: '7px',
                  border: isAutoMode ? '1.5px solid #94a3b8' : '1px solid rgba(255,255,255,0.15)',
                  background: isAutoMode ? 'rgba(148,163,184,0.15)' : 'transparent',
                  cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: '15px', lineHeight: 1 }}>🔄</span>
                <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: isAutoMode ? '#94a3b8' : '#64748b' }}>Auto</span>
              </div>

              {/* Drive */}
              {(() => {
                const locked = MODE_RANK['driving'] < MODE_RANK[movementMode];
                const active = !isAutoMode && effectiveMode === 'driving';
                const cfg    = MODE_CONFIG['driving'];
                return (
                  <div
                    role="button" tabIndex={locked ? -1 : 0}
                    onClick={() => !locked && onSetMode?.('driving')}
                    onKeyDown={e => !locked && e.key === 'Enter' && onSetMode?.('driving')}
                    title={locked ? `Can't override — GPS detects ${movementMode}` : 'Override to Driving mode'}
                    style={{
                      flex: 1,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                      padding: '5px 4px', borderRadius: '7px',
                      border: active ? `1.5px solid ${cfg.color}` : `1px solid ${locked ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.15)'}`,
                      background: active ? `${cfg.color}20` : 'transparent',
                      opacity: locked ? 0.3 : 1,
                      cursor: locked ? 'not-allowed' : 'pointer', userSelect: 'none', transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: '15px', lineHeight: 1 }}>{cfg.icon}</span>
                    <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: active ? cfg.color : locked ? '#475569' : '#64748b' }}>
                      {locked ? '🔒' : 'Drive'}
                    </span>
                  </div>
                );
              })()}

              {/* Fly — never locked, useful for drone/MAVLink missions */}
              {(() => {
                const active = !isAutoMode && effectiveMode === 'flying';
                const cfg    = MODE_CONFIG['flying'];
                return (
                  <div
                    role="button" tabIndex={0}
                    onClick={() => onSetMode?.('flying')}
                    onKeyDown={e => e.key === 'Enter' && onSetMode?.('flying')}
                    title="Override to Flying mode (drone / aircraft)"
                    style={{
                      flex: 1,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                      padding: '5px 4px', borderRadius: '7px',
                      border: active ? `1.5px solid ${cfg.color}` : '1px solid rgba(255,255,255,0.15)',
                      background: active ? `${cfg.color}20` : 'transparent',
                      cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: '15px', lineHeight: 1 }}>{cfg.icon}</span>
                    <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: active ? cfg.color : '#64748b' }}>Fly</span>
                  </div>
                );
              })()}

            </div>
            </>

          ) : (

            /* STATE 2 — has location, Stay Live off: Update button + small Stay Live below */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '4px' }}>
              <button
                className="primary-button"
                onClick={handleManualUpdate}
                style={{ padding: '8px 14px', fontSize: '14px' }}
              >
                Update Location{' '}
                <span style={{ fontSize: '10px', textTransform: 'none', opacity: 0.75, fontFamily: 'monospace' }}>
                  ({countdown}s)
                </span>
              </button>

              {/* Bottom row: Stay Live + Go Offline side by side */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {/* Stay Live toggle */}
                {onStartLiveTracking ? (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={onStartLiveTracking}
                    onKeyDown={e => e.key === 'Enter' && onStartLiveTracking()}
                    title="Auto-update your position while you move"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '5px 11px',
                      borderRadius: '6px',
                      border: '1px solid rgba(76,175,80,0.45)',
                      background: 'rgba(76,175,80,0.08)',
                      color: '#4CAF50',
                      fontSize: '12px', fontWeight: 600,
                      cursor: 'pointer', userSelect: 'none',
                    }}
                  >
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4CAF50', flexShrink: 0 }} />
                    Stay Live
                  </div>
                ) : <span />}

                {/* Go Offline — returns to "Go LIVE" state */}
                {onGoOffline && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={onGoOffline}
                    onKeyDown={e => e.key === 'Enter' && onGoOffline()}
                    title="Go offline and remove your location"
                    style={{
                      fontSize: '11px', color: '#94a3b8',
                      cursor: 'pointer', userSelect: 'none',
                      textDecoration: 'underline',
                      padding: '4px 2px',
                    }}
                  >
                    Go Offline
                  </div>
                )}
              </div>
            </div>

          )}

          {sessionLink && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999', marginBottom: '5px' }}>
                Session Link
              </div>
              <div style={{
                display: 'flex', alignItems: 'stretch',
                background: '#f4f4f4',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: '8px', overflow: 'hidden',
              }}>
                <span style={{
                  flex: 1, padding: '8px 10px',
                  fontSize: '11px', color: '#444',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  userSelect: 'all', lineHeight: '18px',
                }}>
                  {sessionLink}
                </span>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={copyLink}
                  onKeyDown={e => e.key === 'Enter' && copyLink()}
                  title="Copy link"
                  style={{
                    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '38px',
                    background: copied ? 'rgba(34,197,94,0.15)' : 'rgba(0,0,0,0.04)',
                    borderLeft: '1px solid rgba(0,0,0,0.08)',
                    color: copied ? '#16a34a' : '#666',
                    fontSize: '15px', cursor: 'pointer',
                    transition: 'background 0.2s, color 0.2s',
                    userSelect: 'none',
                  }}
                >
                  {copied ? '✓' : '⎘'}
                </div>
              </div>
            </div>
          )}
          {playerLocation && (
            <div
              style={{
                marginTop: '12px',
                fontSize: '12px',
                color: '#666',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span>
                {revealExactLocation ? (
                  <>
                    Location:{' '}
                    <strong style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600 }}>
                      {playerLocation[1].toFixed(4)}, {playerLocation[0].toFixed(4)}
                    </strong>
                  </>
                ) : (
                  <>
                    Near <strong>{formatApproxLocationLabel(playerLocation[1], playerLocation[0])}</strong>
                    <span style={{ color: '#94a3b8', marginLeft: 6 }}>(precise hidden)</span>
                  </>
                )}
              </span>
              <button
                type="button"
                onClick={() => setRevealExactLocation((v) => !v)}
                title={revealExactLocation
                  ? 'Hide precise coordinates — only show the approximate region'
                  : 'Reveal precise lat/lng — only visible on your screen'}
                aria-pressed={revealExactLocation}
                style={{
                  marginLeft: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  fontSize: 11,
                  lineHeight: 1.3,
                  border: '1px solid rgba(0,0,0,0.15)',
                  borderRadius: 999,
                  background: revealExactLocation ? '#0ea5e9' : '#fff',
                  color: revealExactLocation ? '#fff' : '#334155',
                  cursor: 'pointer',
                }}
              >
                <span aria-hidden>{revealExactLocation ? '🙈' : '👁'}</span>
                {revealExactLocation ? 'Hide' : 'Reveal'}
              </button>
            </div>
          )}
          <div className="filter-section">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'nowrap',
              }}
            >
              <label
                style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                title="Wallets that recently shared a GeoLink location near you (within max distance when your location is on)."
              >
                <input
                  type="checkbox"
                  checked={showOtherUsers}
                  onChange={(e) => onToggleShowUsers(e.target.checked)}
                />
                <span style={{ fontSize: 12 }}>Others on map ({otherUsersCount})</span>
              </label>
              {trustGroupMemberAllowlistCount > 0 && onTrustGroupMapFilterToggle && (
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => onTrustGroupMapFilterToggle(!trustGroupMapFilterActive)}
                  title={
                    trustGroupMapFilterActive
                      ? 'Showing only trust group contacts (compact pins). Click to show everyone in range.'
                      : 'Show only wallets from your selected trust groups (load groups in Trust Groups first).'
                  }
                  aria-pressed={trustGroupMapFilterActive}
                  style={{
                    width: 28,
                    height: 28,
                    padding: 0,
                    flexShrink: 0,
                    fontSize: 14,
                    lineHeight: 1,
                    borderRadius: 8,
                    border: trustGroupMapFilterActive ? '1.5px solid #6366f1' : '1px solid rgba(0,0,0,0.12)',
                    background: trustGroupMapFilterActive ? 'rgba(99,102,241,0.18)' : 'rgba(0,0,0,0.04)',
                    color: trustGroupMapFilterActive ? '#4338ca' : '#64748b',
                  }}
                >
                  🛡
                </button>
              )}
            </div>
            {typeof aiAgentsCount === 'number' && (
              <div
                title="AI agents currently posting positions in the watched countries. Always shown — agents don't need your location to appear on the map."
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span aria-hidden>🤖</span>
                <span>AI agents ({aiAgentsCount})</span>
              </div>
            )}
            {showOtherUsers && (
              <>
                <label style={{ marginTop: '12px', display: 'block' }}>
                  Max Distance: {maxDistance} km
                </label>
                <input
                  type="range"
                  min="100"
                  max="20000"
                  step="100"
                  value={Math.min(maxDistance, 20000)}
                  onChange={(e) => onDistanceChange(Number(e.target.value))}
                />
                <div className="range-label">
                  <span>100 km</span>
                  <span>20,000 km</span>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

